<template>
    <b-modal :id="modalId" size="xl" scrollable :hide-footer="!selectionMode" body-class="p-0 m-0" @hidden="resetModal">
        <template #modal-header>
            <div class="d-flex align-items-center justify-content-between w-100">
                <div class="d-flex align-items-center">
                    <div class="avatar-content rounded-circle bg-light-primary p-1 mr-1">
                        <feather-icon icon="FolderIcon" size="18" class="text-primary" />
                    </div>
                    <div>
                        <h4 class="mb-0">{{ folderName }}</h4>
                        <small class="text-muted">Dosya Yönetimi</small>
                    </div>
                </div>

                <div class="d-flex align-items-center">
                    <div v-if="folders && folders.length > 0" class="mr-1" style="width: 200px">
                        <v-select-list
                            v-model="selectedFolderId"
                            :options="folders"
                            :reduce="(f) => f.Id"
                            label="FolderName"
                            placeholder="Klasör Seçin"
                            class="mb-0"
                            @input="$emit('folder-change', $event)"
                        />
                    </div>

                    <b-input-group class="mr-1" style="width: 200px">
                        <b-input-group-prepend is-text>
                            <feather-icon icon="SearchIcon" />
                        </b-input-group-prepend>
                        <b-form-input v-model="searchQuery" placeholder="Dosya ara..." />
                    </b-input-group>

                    <b-button v-if="!selectionMode" variant="primary" size="sm" @click="toggleUpload">
                        <feather-icon :icon="showUpload ? 'MinusIcon' : 'UploadIcon'" size="14" class="mr-50" />
                        {{ showUpload ? "Yüklemeyi Gizle" : "Dosya Yükle" }}
                    </b-button>

                    <feather-icon icon="XIcon" size="24" class="cursor-pointer ml-1 text-muted" @click="$bvModal.hide(modalId)" />
                </div>
            </div>
        </template>

        <div class="file-manager-container p-2">
            <b-collapse v-model="showUpload" class="mb-3">
                <b-card class="mb-0 border shadow-none theme-item">
                    <file-upload-form :key="uploadFormKey" v-model="localUploadData" :languages="languages" @files-selected="handleFilesSelected" />
                    <div class="d-flex justify-content-end align-items-center mt-2 border-top pt-2">
                        <b-button variant="flat-secondary" size="sm" class="mr-1" @click="showUpload = false"> Vazgeç </b-button>
                        <v-executer
                            :loading="uploadLoading"
                            :disabled="!selectedFiles || selectedFiles.length === 0"
                            :action="triggerUpload"
                            text="Yüklemeyi Başlat"
                            icon="UploadCloudIcon"
                            class="btn-sm"
                        />
                    </div>
                </b-card>
            </b-collapse>

            <div v-if="loading" class="text-center py-5">
                <b-spinner variant="primary" />
                <p class="mt-2 text-muted">Dosyalar yükleniyor...</p>
            </div>

            <div v-else>
                <div v-if="files && files.length > 0">
                    <b-row>
                        <b-col v-for="file in filteredFiles" :key="file.Id" cols="6" md="4" lg="3" xl="2">
                            <div
                                class="file-card border rounded shadow-sm position-relative hover-card theme-item file-card-fixed"
                                :class="{ 'border-primary bg-light-primary': isSelected(file) }"
                                @click="selectionMode ? toggleSelection(file) : null"
                                :style="selectionMode ? 'cursor: pointer' : ''"
                            >
                                <div v-if="selectionMode && isSelected(file)" class="position-absolute" style="top: 10px; right: 10px; z-index: 10">
                                    <b-badge variant="primary" class="p-50 rounded-circle">
                                        <feather-icon icon="CheckIcon" size="16" />
                                    </b-badge>
                                </div>
                                <div class="file-preview d-flex align-items-center justify-content-center bg-light rounded-top" style="height: 120px; overflow: hidden">
                                    <img
                                        v-if="file.FileType === 'image' && (file.FilePath || file.ThumbnailPath)"
                                        :src="getFileUrl(file.ThumbnailPath || file.FilePath)"
                                        :alt="file.Alt || file.Title || file.FileName"
                                        class="img-fluid"
                                        style="height: 100%; width: 100%; object-fit: cover"
                                    />
                                    <feather-icon v-else :icon="getFileIcon(file.FileType)" size="42" class="text-muted" />
                                </div>

                                <div class="p-1 file-card-content">
                                    <h6 class="text-truncate mb-1 font-weight-bolder text-dark" :title="file.Title || file.OriginalFileName" style="font-size: 0.85rem">
                                        {{ file.Title || file.OriginalFileName }}
                                    </h6>
                                    <small v-if="!file.Title && file.FileName" class="text-muted d-block text-truncate mb-50" style="font-size: 0.65rem" :title="file.FileName">
                                        {{ file.FileName }}
                                    </small>
                                    <div class="d-flex justify-content-between align-items-center mb-25">
                                        <small class="text-muted" style="font-size: 0.7rem">{{ formatSize(file.FileSize) }}</small>
                                        <b-badge v-if="file.LanguageCode" variant="light-info" style="font-size: 0.65rem">{{ file.LanguageCode }}</b-badge>
                                    </div>
                                    <small v-if="file.FileType === 'image' && file.Width && file.Height" class="text-muted d-block" style="font-size: 0.7rem">
                                        <feather-icon icon="MaximizeIcon" size="10" class="mr-25" />
                                        {{ file.Width }}x{{ file.Height }}
                                    </small>
                                </div>

                                <div v-if="!selectionMode" class="file-actions p-1 border-top d-flex justify-content-end align-items-center bg-transparent">
                                    <feather-icon
                                        icon="EyeIcon"
                                        size="14"
                                        class="cursor-pointer text-secondary hover-scale mr-50"
                                        v-b-tooltip.hover
                                        title="Önizle"
                                        @click.stop="openPreviewModal(file)"
                                    />
                                    <feather-icon
                                        icon="Edit2Icon"
                                        size="14"
                                        class="cursor-pointer text-primary hover-scale mr-50"
                                        v-b-tooltip.hover
                                        title="Düzenle"
                                        @click.stop="openEditModal(file)"
                                    />
                                    <feather-icon icon="TrashIcon" size="14" class="cursor-pointer text-danger hover-scale" v-b-tooltip.hover title="Sil" @click.stop="$emit('delete-file', file)" />
                                </div>
                            </div>
                        </b-col>
                    </b-row>
                </div>

                <div v-else class="text-center py-5 theme-item border rounded">
                    <feather-icon icon="ImageIcon" size="48" class="text-muted mb-3" />
                    <h5 class="text-muted">Bu klasörde dosya yok</h5>
                    <p class="text-muted small">Yeni dosya yüklemek için yukarıdaki butonu kullanın.</p>
                </div>
            </div>
        </div>
        <b-modal id="inner-file-edit-modal" title="Dosya Düzenle" size="xl" append-to-body no-close-on-backdrop>
            <file-edit-form v-if="editingFileItem" v-model="editingFileItem" :languages="languages" />
            <template #modal-footer>
                <v-executer :loading="fileUpdateLoading" :action="triggerUpdateFile" text="Değişiklikleri Kaydet" />
            </template>
        </b-modal>

        <b-modal id="inner-file-preview-modal" hide-footer centered size="lg" body-class="p-0" title="Önizleme">
            <div v-if="previewFileItem" class="text-center bg-dark">
                <img v-if="previewFileItem.FileType === 'image'" :src="getFileUrl(previewFileItem.FilePath)" class="img-fluid" style="max-height: 80vh" />
                <div v-if="previewFileItem.FileType === 'image'" class="p-50 bg-dark">
                    <small class="text-muted">{{ previewFileItem.MimeType }} | {{ formatSize(previewFileItem.FileSize) }}</small>
                </div>
                <div v-else class="p-5 text-white">
                    <feather-icon :icon="getFileIcon(previewFileItem.FileType)" size="64" />
                    <h4 class="mt-3 text-white">{{ previewFileItem.Title }}</h4>
                    <p>{{ previewFileItem.OriginalFileName }}</p>
                    <small v-if="previewFileItem.MimeType" class="text-muted d-block mb-2"> Tip: {{ previewFileItem.MimeType }} </small>
                    <b-button variant="primary" :href="previewFileItem.FilePath" target="_blank"> Dosyayı İndir / Görüntüle </b-button>
                </div>
            </div>
        </b-modal>
        <template #modal-footer>
            <div class="d-flex justify-content-between align-items-center w-100">
                <div class="d-flex align-items-center">
                    <span class="text-primary font-weight-bold mr-1" v-if="selectedGalleryItems.length > 0"> {{ selectedGalleryItems.length }} dosya seçildi </span>
                    <span class="text-muted" v-else>Dosya seçilmedi</span>
                </div>

                <div>
                    <b-button variant="primary" :disabled="selectedGalleryItems.length === 0" @click="confirmSelection">
                        <feather-icon icon="CheckIcon" size="14" class="mr-50" />
                        Seçilenleri Ekle
                    </b-button>
                </div>
            </div>
        </template>
    </b-modal>
</template>

<script>
import { BModal, BButton, BInputGroup, BInputGroupPrepend, BFormInput, BRow, BCol, BCollapse, BCard, BSpinner, BBadge, VBTooltip } from "bootstrap-vue";
import FileUploadForm from "../components/FileUploadForm.vue";
import FileEditForm from "../components/FileEditForm.vue";
import VExecuter from "@/components/VExecuter.vue";
import Utilities from "@/utilities/system/index";
import WebsiteApi from "@/api/website";
import { watch } from "vue";

export default {
    name: "FileManagerModal",
    components: {
        BModal,
        BButton,
        BInputGroup,
        BInputGroupPrepend,
        BFormInput,
        BRow,
        BCol,
        BCollapse,
        BCard,
        BSpinner,
        BBadge,
        FileUploadForm,
        VExecuter,
        FileEditForm,
    },
    directives: {
        "b-tooltip": VBTooltip,
    },
    props: {
        modalId: { type: String, default: "file-manager-modal" },
        folderName: { type: String, default: "Klasör" },
        files: { type: Array, default: () => [] },
        loading: { type: Boolean, default: false },
        languages: { type: Array, default: () => [] },
        uploadLoading: { type: Boolean, default: false },
        fileUpdateLoading: { type: Boolean, default: false },
        selectionMode: { type: Boolean, default: false },
        folders: { type: Array, default: () => [] },
        folderId: { type: [String, Number], default: null },
    },
    data() {
        return {
            searchQuery: "",
            showUpload: false,
            selectedFiles: [],
            uploadFormKey: 0,
            localUploadData: {
                fileKey: "",
                fileType: null,
                title: "",
                alt: "",
                description: "",
                sortOrder: 0,
                isActive: true,
                languageCode: "tr",
                tags: "",
                translations: [],
            },
            editingFileItem: null,
            previewFileItem: null,
            selectedGalleryItems: [],
            selectedFolderId: null,
        };
    },
    computed: {
        filteredFiles() {
            if (!this.searchQuery) return this.files;
            const query = this.searchQuery.toLowerCase();
            return this.files.filter((f) => (f.Title && f.Title.toLowerCase().includes(query)) || (f.FileName && f.FileName.toLowerCase().includes(query)));
        },
    },
    watch: {
        folderId: {
            immediate: true,
            handler(val) {
                this.selectedFolderId = val;
            },
        },
    },
    methods: {
        toggleUpload() {
            this.showUpload = !this.showUpload;
        },
        resetModal() {
            this.searchQuery = "";
            this.showUpload = false;
            this.selectedFiles = [];
            this.selectedGalleryItems = [];
            this.selectedFiles = [];
            this.resetUploadForm();
            this.$emit("hidden");
        },
        isSelected(file) {
            return this.selectedGalleryItems.some((item) => item.Id === file.Id);
        },
        toggleSelection(file) {
            const index = this.selectedGalleryItems.findIndex((item) => item.Id === file.Id);
            if (index > -1) {
                this.selectedGalleryItems.splice(index, 1);
            } else {
                this.selectedGalleryItems.push(file);
            }
        },
        confirmSelection() {
            this.$emit("selection-confirmed", this.selectedGalleryItems);
            this.$bvModal.hide(this.modalId);
            this.selectedGalleryItems = [];
        },
        resetUploadForm() {
            this.localUploadData = {
                fileKey: "",
                fileType: null,
                title: "",
                alt: "",
                description: "",
                sortOrder: 0,
                isActive: true,
                languageCode: "tr",
                tags: "",
                translations: [],
            };
        },

        getFileUrl(path) {
            if (!path) return "";
            if (path.startsWith("http")) return path;

            let baseUrl = Utilities.BaseURL.URL;
            if (!baseUrl || baseUrl === "undefined" || baseUrl === "") {
                baseUrl = "https://localhost:7287";
            }

            return `${baseUrl.replace(/\/$/, "")}/${path.replace(/^\//, "")}`;
        },
        onUploadSuccess() {
            this.uploadFormKey += 1;
            this.showUpload = false;
            this.selectedFiles = [];
            this.resetUploadForm();
        },
        closeEditModal() {
            this.$bvModal.hide("inner-file-edit-modal");
            this.editingFileItem = null;
        },
        handleFilesSelected(files) {
            this.selectedFiles = files;
        },
        triggerUpload() {
            this.$emit("upload-files", {
                files: this.selectedFiles,
                metadata: this.localUploadData,
            });
        },
        getFileIcon(type) {
            const icons = {
                image: "ImageIcon",
                video: "VideoIcon",
                audio: "MusicIcon",
                pdf: "FileTextIcon",
                document: "FileIcon",
            };
            return icons[type] || "FileIcon";
        },
        formatSize(bytes) {
            if (bytes === 0) return "0 B";
            const k = 1024;
            const sizes = ["B", "KB", "MB", "GB"];
            const i = Math.floor(Math.log(bytes) / Math.log(k));
            return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + " " + sizes[i];
        },
        formatDuration(seconds) {
            if (!seconds) return "";
            const m = Math.floor(seconds / 60);
            const s = seconds % 60;
            return `${m}:${s.toString().padStart(2, "0")}`;
        },
        openEditModal(file) {
            this.editingFileItem = {
                id: file.Id,
                fileKey: file.FileKey,
                title: file.Title,
                alt: file.Alt,
                description: file.Description,
                sortOrder: file.SortOrder,
                isActive: file.IsActive,
                tags: file.Tags,
                translations: file.Translations ? JSON.parse(JSON.stringify(file.Translations)) : [],
                metaData: file.MetaData ? JSON.parse(JSON.stringify(file.MetaData)) : [],
            };

            this.$bvModal.show("inner-file-edit-modal");
        },
        triggerUpdateFile() {
            this.$emit("update-file", this.editingFileItem);
        },
        openPreviewModal(file) {
            this.previewFileItem = file;
            this.$bvModal.show("inner-file-preview-modal");
            WebsiteApi.Gallery.IncrementViewCount(file.Id);
        },
        handleDownload(file) {
            WebsiteApi.Gallery.IncrementDownloadCount(file.Id);
            window.open(this.getFileUrl(file.FilePath), "_blank");
        },
    },
};
</script>

<style scoped>
.theme-item {
    background-color: #ffffff;
    transition: all 0.3s ease;
}
.dark-layout .theme-item {
    background-color: #161d31 !important;
    border-color: #3b4253 !important;
}
.dark-layout .theme-item .text-dark {
    color: #d0d2d6 !important;
}
.dark-layout .theme-item .text-muted {
    color: #676d7d !important;
}

.hover-card {
    transition: all 0.2s ease;
    border: 1px solid #e2e2e2 !important;
}
.hover-card:hover {
    border-color: #7367f0 !important;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1) !important;
    transform: translateY(-2px);
}
.dark-layout .hover-card:hover {
    background-color: #202943 !important;
    box-shadow: 0 5px 20px rgba(0, 0, 0, 0.6) !important;
    border-color: #7367f0 !important;
}

.file-preview {
    background-color: #f8f9fa;
}
.dark-layout .file-preview {
    background-color: #283046;
}

.hover-scale:hover {
    transform: scale(1.2);
}
.border-dashed {
    border-style: dashed !important;
}
.file-card-content {
    min-height: 85px;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
}

.file-card-fixed {
    height: 270px !important;
    display: flex;
    flex-direction: column;
}

.file-card-fixed .file-preview {
    height: 120px;
    flex-shrink: 0;
}

.file-card-fixed .file-card-content {
    flex: 1;
    display: flex;
    flex-direction: column;
    min-height: 0;
    overflow: hidden;
}

.file-card-fixed .file-actions {
    height: 40px;
    flex-shrink: 0;
}
</style>
